import { useCallback } from "react"

import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { getNotificationIconUrl } from "@/hooks/use-favicon"
import { logger } from "@/lib/logger"

import { type ChatStore, claimChatRequestCompletionNotification } from "./chat-request-registry"

type UseBackgroundChatCompletionProps = {
  chatStore: ChatStore
  fallbackSessionType: string
  getRouteChatPath: (routeId: string) => string
}

type CompletedChatRequest = {
  requestToken: symbol
  sessionId: string
}

function normalizePathname(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname
}

function isDocumentFocused(): boolean {
  try {
    return document.hasFocus()
  } catch {
    return false
  }
}

function isOriginatingChatVisible(targetPath: string): boolean {
  try {
    return (
      normalizePathname(window.location.pathname) === normalizePathname(targetPath) &&
      !document.hidden &&
      isDocumentFocused()
    )
  } catch {
    return false
  }
}

function canShowBrowserNotification(): boolean {
  try {
    return typeof window.Notification === "function" && window.Notification.permission === "granted"
  } catch {
    return false
  }
}

export function useBackgroundChatCompletion({
  chatStore,
  fallbackSessionType,
  getRouteChatPath,
}: UseBackgroundChatCompletionProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return useCallback(
    ({ requestToken, sessionId }: CompletedChatRequest) => {
      if (!claimChatRequestCompletionNotification(chatStore, sessionId, requestToken)) {
        return
      }

      const store = chatStore.getState()
      const session = store.sessions.find((candidate) => candidate?.id === sessionId)
      const sessionType = session?.sessionType ?? fallbackSessionType
      const chatTitle =
        session?.title?.trim() || t("notifications.chat_response_complete.fallback_chat_title")
      const targetPath = `${getRouteChatPath(sessionType)}/${encodeURIComponent(sessionId)}`

      // Claim the turn before checking visibility. That makes foreground
      // completion final: navigating away later cannot retroactively notify.
      if (isOriginatingChatVisible(targetPath)) return

      const title = t("notifications.chat_response_complete.title")
      const body = t("notifications.chat_response_complete.body", { chatTitle })
      const openChat = () => {
        try {
          window.focus()
        } catch {
          // Best effort: navigation can still succeed.
        }

        chatStore.getState().clearSessionResponseUnread(sessionId)
        try {
          void navigate(targetPath)
        } catch {
          logger.warn("Failed to open completed background chat", { sessionId })
        }
      }

      store.markSessionResponseUnread(sessionId)

      try {
        toast.success(title, {
          description: body,
          action: {
            label: t("notifications.chat_response_complete.view_action"),
            onClick: openChat,
          },
        })
      } catch {
        logger.warn("Failed to show background chat completion toast", { sessionId })
      }

      if (!document.hidden && isDocumentFocused()) return
      if (!canShowBrowserNotification()) return

      try {
        const notification = new window.Notification(title, {
          body,
          icon: getNotificationIconUrl(),
          silent: false,
        })
        notification.onclick = () => {
          openChat()
          notification.close()
        }
      } catch {
        logger.warn("Failed to show browser chat completion notification", { sessionId })
      }
    },
    [chatStore, fallbackSessionType, getRouteChatPath, navigate, t]
  )
}
